import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-preview-email-template',
  templateUrl: './preview-email-template.component.html',
  styleUrls: ['./preview-email-template.component.scss']
})
export class PreviewEmailTemplateComponent {

  sanitizedData: SafeHtml = '';  // SafeHtml for srcdoc binding
 @Input() type: string = 'email';
 @Input() attachments: any[] = [];
 @Input() templateType: string = 'html';  // 'text' or 'html'
 previewAttachments: any[] = [];
   selectedIndex: any;
@Output() close: EventEmitter<any> = new EventEmitter();

  // Lightbox state
  lightboxOpen = false;
  lightboxIndex = 0;
  lightboxAttachment: any = null;
  lightboxType: 'image' | 'pdf' | 'video' | 'other' = 'other';

  constructor(
    private sanitizer: DomSanitizer,
    private modalService: NgbModal
  ) {}

    @Input() processedData: any;

    ngOnInit() {
      // Both 'text' and 'html' template bodies are stored as HTML (from contenteditable/textarea)
      // Render both directly — no escaping needed
      if (this.processedData && typeof this.processedData === 'string') {
        const htmlContent = `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>body{font-family:Arial,sans-serif;margin:20px;line-height:1.6;color:#333;}</style></head><body>${this.processedData}</body></html>`;
        this.sanitizedData = this.sanitizer.bypassSecurityTrustHtml(htmlContent);
      } else {
        // Already a SafeHtml value (sanitized upstream)
        this.sanitizedData = this.processedData;
      }
    }

    // Escape HTML special characters for text display
    escapeHtml(text: string): string {
      const map: { [key: string]: string } = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      };
      return text.replace(/[&<>"']/g, (m) => map[m]);
    }
    
    closePopup() { this.close.emit() }

    // ── Lightbox ────────────────────────────────────────────────────────────
    openLightbox(index: number) {
      this.lightboxIndex = index;
      this.lightboxAttachment = this.attachments[index];
      this.lightboxType = this.getLightboxType(this.lightboxAttachment?.fileName);
      this.lightboxOpen = true;
      document.body.style.overflow = 'hidden';
    }

    closeLightbox() {
      this.lightboxOpen = false;
      this.lightboxAttachment = null;
      document.body.style.overflow = '';
    }

    lightboxNav(direction: number) {
      const next = this.lightboxIndex + direction;
      if (next >= 0 && next < this.attachments.length) {
        this.openLightbox(next);
      }
    }

    getLightboxType(fileName: string): 'image' | 'pdf' | 'video' | 'other' {
      if (!fileName) return 'other';
      const ext = fileName.split('.').pop()?.toLowerCase() || '';
      if (['jpg','jpeg','png','gif','webp','bmp','svg'].includes(ext)) return 'image';
      if (ext === 'pdf') return 'pdf';
      if (['mp4','webm','ogg','mov'].includes(ext)) return 'video';
      return 'other';
    }
    // ────────────────────────────────────────────────────────────────────────

    // Get file icon class based on extension
    getFileIcon(fileName: string): string {
      if (!fileName) return 'fa-file';
      
      const ext = fileName.toLowerCase();
      
      if (ext.endsWith('.pdf')) return 'fa-file-pdf text-danger';
      if (ext.endsWith('.doc') || ext.endsWith('.docx')) return 'fa-file-word text-primary';
      if (ext.endsWith('.xls') || ext.endsWith('.xlsx')) return 'fa-file-excel text-success';
      if (ext.endsWith('.ppt') || ext.endsWith('.pptx')) return 'fa-file-powerpoint text-warning';
      if (ext.endsWith('.jpg') || ext.endsWith('.jpeg') || ext.endsWith('.png') || ext.endsWith('.gif')) return 'fa-file-image text-info';
      
      return 'fa-file text-secondary';
    }
    
    // Preview attachment — kept for backward compat; new code uses openLightbox
    previewAttachment(attachment: any, _previewModal?: any) {
      if (!attachment) { return; }
      const idx = this.attachments.indexOf(attachment);
      this.openLightbox(idx >= 0 ? idx : 0);
    }
    
    // Get file type from filename
    getFileType(fileName: string): string {
      if (!fileName) return 'other';
      
      const ext = fileName.toLowerCase();
      const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
      const videoExts = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv'];
      const audioExts = ['mp3', 'wav', 'ogg', 'aac'];
      
      if (imageExts.some(e => ext.endsWith('.' + e))) return 'image';
      if (ext.endsWith('.pdf')) return 'pdf';
      if (videoExts.some(e => ext.endsWith('.' + e))) return 'video';
      if (audioExts.some(e => ext.endsWith('.' + e))) return 'audio';
      
      return 'other';
    }
    
    // Get MIME type from filename
    getMimeType(fileName: string): string {
      if (!fileName) return 'application/octet-stream';
      
      const ext = fileName.toLowerCase();
      const mimeTypes: { [key: string]: string } = {
        'pdf': 'application/pdf',
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'png': 'image/png',
        'gif': 'image/gif',
        'doc': 'application/msword',
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'xls': 'application/vnd.ms-excel',
        'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'ppt': 'application/vnd.ms-powerpoint',
        'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'mp4': 'video/mp4',
        'mp3': 'audio/mpeg'
      };
      
      for (const [extension, mimeType] of Object.entries(mimeTypes)) {
        if (ext.endsWith('.' + extension)) {
          return mimeType;
        }
      }
      
      return 'application/octet-stream';
    }
    
}
